/// <amd-module name="Artifacts/FeedSettings/services/FeedSettingsService" />

import { getViewPermissionsDelta } from "Artifacts/FeedSettings/helpers/PermissionsHelpers";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { IDetailsTabFormState } from "Artifacts/FeedSettings/types/IDetailsTabFormState";
import * as Constants from "Artifacts/Root/Constants";
import { loadNonNullData } from "Artifacts/Root/helpers/DataProviderHelpers";
import { getFeedIdentifier } from "Artifacts/Root/helpers/FeedHelpers";
import { getCurrentSelectedProjectId } from "Artifacts/Root/helpers/ProjectHelpers";
import { IdentityDataService } from "Artifacts/Root/services/IdentityDataService";
import { IFeed } from "Artifacts/Root/types";
import { ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import {
  Feed,
  FeedPermission,
  FeedRetentionPolicy,
  FeedRole,
  FeedView,
  FeedViewType,
  UpstreamSource,
} from "Artifacts/Root/web-apis/contracts/Feed";
import { getFeedClient, IFeedRestClient } from "Artifacts/Root/web-apis/RestClient/Feed";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IServiceEndpointRestClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";

export interface IFeedSettingsService extends IVssService {
  getInitialFeedData(pageContext: IVssPageContext, feed: IFeed): Promise<IFeedSettings>;
  getFeedPermissionsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view?: ViewIdentifier,
    includeIds?: boolean,
    excludeInheritedPermissions?: boolean,
    identityDescriptors?: string,
    includeDeleted?: boolean,
  ): Promise<FeedPermission[]>;
  updateFeedDetailsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    feedDetails: IDetailsTabFormState,
  ): Promise<void>;
  setFeedPermissionsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    permissions: FeedPermission[],
  ): Promise<FeedPermission[]>;
  updateUpstreamSources(
    pageContext: IVssPageContext,
    feed: IFeed,
    upstreams: UpstreamSource[],
  ): Promise<UpstreamSource[]>;
  getServiceConnectionsForUpstreaming(pageContext: IVssPageContext): Promise<ServiceEndpoint[]>;
  addUpdateFeedViewAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
    isEdit: boolean,
  ): Promise<FeedView>;
  updateDefaultFeedViewAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
  ): Promise<Feed>;
  deleteFeedViewAsync(pageContext: IVssPageContext, feed: IFeed, view: FeedView): Promise<void>;
  setFeedViewPermissionsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
    existingPermissions: FeedPermission[],
    permissionsToSet: FeedPermission[],
  ): Promise<FeedPermission[]>;
}

export class FeedSettingsService extends VssService implements IFeedSettingsService {
  public async getInitialFeedData(
    pageContext: IVssPageContext,
    feed: IFeed,
  ): Promise<IFeedSettings> {
    return loadNonNullData<IFeedSettings>(
      pageContext,
      "ms.azure-artifacts.feed-settings-data-provider",
      { feedId: feed.id, projectName: feed.project?.name, supportServicePrincipals: false },
      true /* ignoreCache */,
      Constants.CommonConstants.FeedServiceInstanceId,
    );
  }

  public async getFeedPermissionsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view?: ViewIdentifier,
    includeIds?: boolean,
    excludeInheritedPermissions?: boolean,
    identityDescriptors?: string,
    includeDeleted?: boolean,
  ): Promise<FeedPermission[]> {
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);

    const projectId = feed.project?.id;
    const feedIdOrName = getFeedIdentifier(feed, view);

    return feedRestClient.getFeedPermissions(
      feedIdOrName,
      projectId,
      includeIds,
      excludeInheritedPermissions,
      identityDescriptors,
      includeDeleted,
    );
  }

  public async updateUpstreamSources(
    pageContext: IVssPageContext,
    feed: IFeed,
    upstreams: UpstreamSource[],
  ): Promise<UpstreamSource[]> {
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);

    const feedPatch = {
      id: feed.id,
      upstreamSources: upstreams.filter((s) => !s.deletedDate), // important to exclude deleted sources
      upstreamEnabled: true,
      project: feed.project,
    } as Feed;

    const updatedFeed: Feed = await feedRestClient.updateFeed(feedPatch, feed.id, feed.project?.id);

    return updatedFeed.upstreamSources;
  }

  public async getServiceConnectionsForUpstreaming(
    pageContext: IVssPageContext,
  ): Promise<ServiceEndpoint[]> {
    const client = pageContext.getRestClient<IServiceEndpointRestClient>(
      "ServiceEndpoint.IServiceEndpointRestClient",
    );

    const projectId = getCurrentSelectedProjectId(pageContext);
    if (!projectId) {
      throw new Error("Unable to get current project ID to retrieve service connections.");
    }

    return client.getServiceEndpoints(projectId, "artifacts-upstream");
  }

  /**
   * Saves feed details, as well as retention policies
   * @param pageContext
   * @param feed
   * @param feedDetails
   */
  public async updateFeedDetailsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    feedDetails: IDetailsTabFormState,
  ): Promise<void> {
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);

    const feedPatch: Feed = {
      id: feed.id,
      name: feedDetails.feedName,
      description: feedDetails.feedDescription,
      hideDeletedPackageVersions: feedDetails.hideDeletedPackageVersions,
      badgesEnabled: feedDetails.badgesEnabled,
      project: feed.project,
    } as Feed;
    await feedRestClient.updateFeed(feedPatch, feed.id, feed.project?.id);

    if (
      !feedDetails.retentionPoliciesEnabled ||
      feedDetails.retentionPolicyCountLimit === null ||
      +feedDetails.retentionPolicyCountLimit <= 0
    ) {
      await feedRestClient.deleteFeedRetentionPolicies(feed.id, feed.project?.id);
      return;
    }

    const policy = {
      ageLimitInDays: 0,
      countLimit: +feedDetails.retentionPolicyCountLimit,
      daysToKeepRecentlyDownloadedPackages: +feedDetails.retentionPolicyDaysLimit,
    } as FeedRetentionPolicy;

    await feedRestClient.setFeedRetentionPolicies(policy, feed.id, feed.project?.id);
  }

  public async setFeedPermissionsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    permissions: FeedPermission[],
    view?: ViewIdentifier,
  ): Promise<FeedPermission[]> {
    const feedIdOrName = getFeedIdentifier(feed, view);
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);
    return await feedRestClient.setFeedPermissions(permissions, feedIdOrName, feed.project?.id);
  }

  public async addUpdateFeedViewAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
    isEdit: boolean,
  ): Promise<FeedView> {
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);
    let savedView: FeedView;
    if (isEdit) {
      const editPatch: FeedView = {
        id: view.id,
        // do not send a view name for an implicit view
        name: view.type === FeedViewType.Implicit ? null : view.name,
        visibility: view.visibility,
      } as FeedView;
      savedView = await feedRestClient.updateFeedView(
        editPatch,
        feed.id,
        view.id,
        feed.project?.id,
      );
    } else {
      savedView = await feedRestClient.createFeedView(view, feed.id, feed.project?.id);
    }

    return savedView;
  }

  public async updateDefaultFeedViewAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
  ): Promise<Feed> {
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);

    const feedPatch = {
      id: feed.id,
      project: feed.project,
      defaultViewId: view.id,
    } as Feed;

    const updatedFeed: Feed = await feedRestClient.updateFeed(feedPatch, feed.id, feed.project?.id);
    return updatedFeed;
  }

  public async deleteFeedViewAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
  ): Promise<void> {
    const feedRestClient: IFeedRestClient = getFeedClient(pageContext);
    await feedRestClient.deleteFeedView(feed.id, view.id, feed.project?.id);
  }

  public async setFeedViewPermissionsAsync(
    pageContext: IVssPageContext,
    feed: IFeed,
    view: FeedView,
    existingPermissions: FeedPermission[],
    permissionsToSet: FeedPermission[],
  ): Promise<FeedPermission[]> {
    const { permissionsToRetain, permissionsToAdd, permissionsToDelete } = getViewPermissionsDelta(
      existingPermissions,
      permissionsToSet,
    );

    if (permissionsToAdd.length > 0) {
      const identityDataService: IdentityDataService = this.pageContext.getService(
        "IIdentityDataService",
      );

      const identityIds: string[] = [];
      permissionsToAdd.forEach((permission: FeedPermission) => {
        identityIds.push(permission.identityId);
      });

      // need to get descriptors for recently added user/group because IdentityPicker
      // doesn't return descriptors
      const identities: Identity[] = await identityDataService.readIdentitiesAsync(identityIds);
      identities.forEach((identity: Identity, index: number) => {
        permissionsToAdd[index].identityDescriptor = identity.descriptor;
      });
    }

    if (permissionsToDelete.length > 0) {
      // set role to None for deleted permissions
      permissionsToDelete.forEach((permission: FeedPermission) => {
        permission.role = FeedRole.None;
      });
    }

    const permissionsToSend = [...permissionsToAdd, ...permissionsToDelete];
    if (permissionsToSend.length > 0) {
      await this.setFeedPermissionsAsync(pageContext, feed, permissionsToSend, view);
    }

    return permissionsToRetain.concat(permissionsToAdd);
  }
}

Services.add("IFeedSettingsService", { serviceFactory: FeedSettingsService });
